import type { EntityState } from '@core/ngrx-legacy';
import { createEntityCollectionStore, getEntityCollectionActions } from '@core/ngrx-legacy';
import { InitialDataChecksumToken } from '@core/reports-types';

import type {
  FetchOneReportResultsFailedPayload,
  FetchOneReportResultsPayload,
  FetchOneReportResultsSuccessPayload,
  ReportResultsEntity,
} from './results.types';
import { safeReportsStateSelector } from '../helpers/safe-selector';

const selectDataId = (data: { paramsHash: string }) => data.paramsHash;

const initialData = (): ReportResultsEntity => ({
  paramsHash: InitialDataChecksumToken,
  response: {
    checksum: InitialDataChecksumToken,
    meta: {},
    results: {
      columns: [],
      rows: [],
    },
  },
});

const { actions, selectors, reducer, namespace, initialState } =
  createEntityCollectionStore<ReportResultsEntity>()('ReportsResults', {
    idSelector: selectDataId,
    baseSelector: (root): EntityState<ReportResultsEntity> =>
      safeReportsStateSelector(root, namespace, initialState),

    customActions: adapter => {
      const {
        createOneOptimistic,
        updateOneOptimistic,
        createOneOptimisticSuccess,
        createOneOptimisticFailed,
      } = getEntityCollectionActions(adapter, selectDataId);

      return {
        fetchOneResult: (state, payload: FetchOneReportResultsPayload) =>
          state.entityMap[payload.data.paramsHash]
            ? updateOneOptimistic(state, {
                correlationId: payload.data.paramsHash,
                data: state.entityMap[payload.data.paramsHash]!,
              })
            : createOneOptimistic(state, {
                correlationId: payload.data.paramsHash,
                data: initialData(),
              }),
        fetchOneResultSuccess: (state, payload: FetchOneReportResultsSuccessPayload) =>
          createOneOptimisticSuccess(state, {
            correlationId: payload.data.paramsHash,
            data: payload.data,
          }),

        fetchOneResultFailed: (state, payload: FetchOneReportResultsFailedPayload) =>
          createOneOptimisticFailed(state, {
            correlationId: payload.data.paramsHash,
            data: <any>{
              paramsHash: payload.data.paramsHash,
            },
            error: <any>payload.data.error,
          }),
      };
    },
  });

const selectResultById =
  (id: string) =>
  (state: EntityState<ReportResultsEntity>): ReportResultsEntity =>
    selectors.byId(id)(state) ?? initialData();

export const ReportsResultsSelectors = {
  byId: selectResultById,
  meta: selectors.meta,
};

export {
  actions as ReportsResultsActions,
  reducer as ReportsResultsReducer,
  namespace as ReportsResultsNamespace,
};
